import { Box, Paper } from "@mui/material";
import React, { useMemo } from "react";
import { PUZZLE_GRID_COLORS } from "../../../services/ColorService";

const AdminPuzGrid = ({
    grid = [],
    gridSize,
    filledCoordinates = [],
    highlightCells = [],
    onCellClick,
    showWordNumbers = true, // Default to true for backward compatibility
    backgroundColor,
}) => {
    // Prefer actual matrix shape so Mini (e.g. 9×6) cells stay square; gridSize is for square boards (Word Wheel).
    const rowCount = grid.length > 0 ? grid.length : (gridSize || 12);
    const colCount =
        grid.length > 0 && Array.isArray(grid[0]) && grid[0].length > 0
            ? grid[0].length
            : (gridSize || rowCount);
    // Get the first character position of a word
    const getFirstPosition = (coord) => {
        if (!coord.positions || coord.positions.length === 0) return null;

        // Exclude hidden message words
        if (coord.direction === "scattered") {
            return null;
        }

        let firstPos;

        if (coord.reversed) {
            // Reversed word: first character of word text is at the last position in positions array
            firstPos = coord.positions[coord.positions.length - 1];
        } else {
            // Normal word: get first position based on direction
            if (coord.direction === "vertical") {
                firstPos = coord.positions.reduce((min, pos) => pos.row < min.row ? pos : min);
            } else if (coord.direction === "horizontal") {
                firstPos = coord.positions.reduce((min, pos) => pos.col < min.col ? pos : min);
            } else {
                // For diagonal directions, use the first position in the array
                firstPos = coord.positions[0];
            }
        }

        return firstPos;
    };


    // Calculate word numbers: assign numbers to words based on their starting positions
    // Words are ordered left-to-right, top-to-bottom
    // Words that share the same starting cell get the same number
    const wordNumbers = useMemo(() => {
        if (!filledCoordinates || filledCoordinates.length === 0) {
            return new Map(); // Map of "row,col" -> number
        }

        // Collect all unique starting positions
        const startingPositions = new Map(); // Map of "row,col" -> {row, col}

        filledCoordinates.forEach(coord => {
            // Skip hidden message words
            if (coord.direction === "scattered") {
                return;
            }

            const firstPos = getFirstPosition(coord);
            if (!firstPos) return;

            const cellKey = `${firstPos.row},${firstPos.col}`;

            // Store the position if not already stored
            if (!startingPositions.has(cellKey)) {
                startingPositions.set(cellKey, { row: firstPos.row, col: firstPos.col });
            }
        });

        // Sort positions: top-to-bottom (by row), then left-to-right (by column)
        const sortedPositions = Array.from(startingPositions.values()).sort((a, b) => {
            if (a.row !== b.row) {
                return a.row - b.row; // Top to bottom
            }
            return a.col - b.col; // Left to right
        });

        // Assign numbers to sorted positions
        const cellToNumber = new Map();
        sortedPositions.forEach((pos, index) => {
            const cellKey = `${pos.row},${pos.col}`;
            cellToNumber.set(cellKey, index + 1); // Start from 1
        });

        return cellToNumber;
    }, [filledCoordinates]);

    // Get word number for a specific cell
    const getWordNumber = (row, col) => {
        const cellKey = `${row},${col}`;
        return wordNumbers.get(cellKey) || null;
    };

    const isCellHighlighted = (row, col) => {
        return highlightCells.some(cell => cell.row === row && cell.col === col);
    };

    // Get the highlight color for a specific cell - supports per-cell colors
    const getHighlightColor = (row, col) => {
        const cell = highlightCells.find(cell => cell.row === row && cell.col === col);
        // If cell has a color property, use it; otherwise use default orange
        return cell?.color || PUZZLE_GRID_COLORS.ORANGE;
    };

    // Get hover color for a highlighted cell
    const getHighlightHoverColor = (row, col) => {
        const color = getHighlightColor(row, col);
        // If it's a custom color, darken it slightly for hover
        if (color !== PUZZLE_GRID_COLORS.ORANGE) {
            // Simple darkening: convert hex to RGB, reduce brightness
            const hex = color.replace('#', '');
            const r = parseInt(hex.substr(0, 2), 16);
            const g = parseInt(hex.substr(2, 2), 16);
            const b = parseInt(hex.substr(4, 2), 16);
            const darkenedR = Math.max(0, r - 20);
            const darkenedG = Math.max(0, g - 20);
            const darkenedB = Math.max(0, b - 20);
            return `rgb(${darkenedR}, ${darkenedG}, ${darkenedB})`;
        }
        return PUZZLE_GRID_COLORS.ORANGE_HOVER_1;
    };

    return (
        <Paper
            elevation={3}
            sx={{
                p: 1,
                backgroundColor: backgroundColor || PUZZLE_GRID_COLORS.BACKGROUND_LIGHT,
                userSelect: 'none',
                display: 'grid',
                gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${rowCount}, minmax(0, 1fr))`,
                gap: 0.25,
                borderRadius: 1,
                width: '100%',
                maxWidth: '100%',
                height: 'auto',
                maxHeight: '100%',
                // cols/rows keeps each track square for Mini rectangles and Classic squares
                aspectRatio: `${colCount} / ${rowCount}`,
                minWidth: 0,
                minHeight: 0,
                transition: 'all 0.3s ease',
                margin: '0 auto',
                flexShrink: 0,
                boxSizing: 'border-box',
                overflow: 'hidden'
            }}
        >
            {grid.map((row, rowIndex) =>
                row.map((cell, colIndex) => {
                    const isHighlighted = isCellHighlighted(rowIndex, colIndex);
                    const isEmpty = !cell || cell.trim() === '';

                    return (
                        <Box
                            key={`${rowIndex}-${colIndex}`}
                            sx={{
                                width: '100%',
                                height: '100%',
                                minHeight: 0,
                                minWidth: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                backgroundColor: (() => {
                                    if (isHighlighted) {
                                        return getHighlightColor(rowIndex, colIndex);
                                    } else if (isEmpty) {
                                        return '#bdbdbd'; // Gray for empty cells
                                    }
                                    return PUZZLE_GRID_COLORS.WHITE;
                                })(),
                                color: PUZZLE_GRID_COLORS.TEXT_DARK,
                                fontFamily: "'Open Sans', sans-serif",
                                fontSize: 'clamp(0.6rem, 1.5vw, 1rem)',
                                fontWeight: 400,
                                cursor: onCellClick ? 'pointer' : 'default',
                                border: `1px solid ${PUZZLE_GRID_COLORS.BORDER_LIGHT}`,
                                borderRadius: 0.25,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: (() => {
                                        if (isHighlighted) {
                                            return getHighlightHoverColor(rowIndex, colIndex);
                                        }
                                        return PUZZLE_GRID_COLORS.HOVER_BACKGROUND;
                                    })()
                                }
                            }}
                            onClick={() => onCellClick && onCellClick(rowIndex, colIndex)}
                        >
                            {/* Word number circle - only show if showWordNumbers is true */}
                            {showWordNumbers && getWordNumber(rowIndex, colIndex) && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '33.33%',
                                        height: '33.33%',
                                        minWidth: '17px',
                                        minHeight: '17px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#C4FFE0',
                                        borderRadius: '50%',
                                        border: '1px solid #2E8B57',
                                        fontSize: 'clamp(0.4rem, 1.2vw, 0.6rem)',
                                        fontWeight: 600,
                                        color: '#000',
                                        zIndex: 10,
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    {getWordNumber(rowIndex, colIndex)}
                                </Box>
                            )}
                            {cell}
                        </Box>
                    );
                })
            )}
        </Paper>
    );
};

export default AdminPuzGrid;

